// "Rellenar con IA": leer el expediente y traer una PROPUESTA para una pestaña
// técnica. Mismo patrón que el resto de las mutaciones del informe.

import { useMutation } from "@tanstack/react-query";

import {
  rellenarAguasConIa,
  rellenarConstruccionesConIa,
  rellenarDescripcionConIa,
  rellenarInstalacionesConIa,
  rellenarPlantacionesConIa,
  rellenarMaquinariasConIa,
  rellenarSuelosConIa,
  resolverElRelleno,
  type ResultadoDelRelleno,
} from "@/lib/rellenoConIa";

/**
 * Lee el expediente y devuelve la propuesta para el "Descriptor de aguas".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, al revés que todas las demás
 * mutaciones de esta pantalla, y esa ausencia es el punto: este endpoint NO
 * GUARDA NADA. La propuesta va al borrador del formulario y la persona decide.
 * Escribirla en la caché del informe la haría parecer guardada —la pestaña se
 * vería igual que después de un guardado real— y el siguiente refresco la
 * haría desaparecer sin explicación.
 */
export function useRellenarAguasConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarAguasConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para la "Descripción de suelos".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que su gemelo de
 * aguas: este endpoint NO GUARDA NADA. Escribir la propuesta en la caché la
 * haría parecer guardada —la pestaña se vería igual que después de un guardado
 * real— y el siguiente refresco la haría desaparecer sin explicación.
 */
export function useRellenarSuelosConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarSuelosConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para "Maquinarias y equipos".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que sus dos
 * gemelos: este endpoint NO GUARDA NADA. Acá se nota más que en las otras, donde
 * la propuesta va al borrador — la lista de máquinas guardadas sale de la caché
 * del informe, así que escribir la propuesta ahí las haría aparecer en el cuadro
 * como si estuvieran guardadas, y el siguiente refresco las borraría.
 */
export function useRellenarMaquinariasConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarMaquinariasConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para "Instalaciones y obras".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que sus gemelos:
 * este endpoint NO GUARDA NADA, y acá —como en maquinaria— la lista de obras
 * guardadas sale de esa caché.
 */
export function useRellenarInstalacionesConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarInstalacionesConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para "Construcciones".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que sus cuatro
 * gemelos: este endpoint NO GUARDA NADA, y acá —como en maquinaria e
 * instalaciones— la lista de construcciones guardadas sale de esa caché.
 */
export function useRellenarConstruccionesConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarConstruccionesConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para "Descriptor de plantaciones".
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que sus cinco
 * gemelos: este endpoint NO GUARDA NADA, y la lista de plantaciones guardadas
 * sale de esa caché.
 */
export function useRellenarPlantacionesConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarPlantacionesConIa(informeId),
  });
}

/**
 * Lee el expediente y devuelve la propuesta para "Descripción de la
 * propiedad": los deslindes de cada rol que el informe ya tiene cargado.
 *
 * NO INVALIDA NI PARCHEA LA CACHÉ DEL INFORME, por lo mismo que sus seis
 * gemelos: este endpoint NO GUARDA NADA. La propuesta va al borrador de la
 * pestaña y la persona decide.
 */
export function useRellenarDescripcionConIa(informeId: number) {
  return useMutation({
    mutationFn: () => rellenarDescripcionConIa(informeId),
  });
}

/**
 * Registra si la propuesta se usó o se descartó.
 *
 * SU FALLO NO IMPORTA y por eso no tiene `onError`: es auditoría, y perder el
 * registro de una corrida no puede impedirle a nadie guardar su trabajo ni
 * merece un aviso en pantalla que la persona no sabría qué hacer con él. Lo que
 * mide `ia_ejecuciones` es la proporción entre aceptadas y descartadas, y una
 * fila que se pierde de vez en cuando no la mueve.
 */
export function useResolverElRelleno() {
  return useMutation({
    mutationFn: ({
      ejecucionId,
      resultado,
    }: {
      ejecucionId: number;
      resultado: ResultadoDelRelleno;
    }) => resolverElRelleno(ejecucionId, resultado),
  });
}
